import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type {
  AuthState,
  ForgotPasswordRequest,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  User,
  UserRole,
} from '@/types';
import { hasRole, hasAnyRole } from '@/types';
import api from '@/services/api';
import { getAccessToken, setTokens, clearTokens } from '@/services/tokenStore';

interface AuthContextValue extends AuthState {
  error: string | null;
  login: (credentials: LoginRequest) => Promise<User>;
  register: (data: RegisterRequest) => Promise<void>;
  forgotPassword: (data: ForgotPasswordRequest) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  /** Checks if the current user has a specific role. */
  userHasRole: (role: UserRole) => boolean;
  /** Checks if the current user has any of the specified roles. */
  userHasAnyRole: (roles: UserRole[]) => boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication provider managing user state, login, register,
 * forgot-password, and logout flows.
 *
 * - Stores tokens in memory via tokenStore (not localStorage per Ring security standards).
 * - Exposes isLoading and error states for UI feedback.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true); // true until session restore check completes
  const [error, setError] = useState<string | null>(null);

  // On mount: if a token exists in localStorage, restore the session via GET /auth/me.
  // This prevents the page from redirecting to /login after a hard refresh.
  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    api
      .get<User>('/auth/me')
      .then(({ data }) => setUser(data))
      .catch(() => clearTokens())
      .finally(() => setIsLoading(false));
  }, []);

  const isAuthenticated = user !== null;

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const login = useCallback(async (credentials: LoginRequest): Promise<User> => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await api.post<LoginResponse>('/auth/login', credentials);
      setTokens(data.accessToken, data.refreshToken);
      setUser(data.user);
      return data.user;
    } catch (err: unknown) {
      const message = extractErrorMessage(err, 'Credenciais inválidas. Tente novamente.');
      setError(message);
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (data: RegisterRequest) => {
    setIsLoading(true);
    setError(null);
    try {
      await api.post('/auth/register', data);
    } catch (err: unknown) {
      const message = extractErrorMessage(err, 'Erro ao criar conta. Tente novamente.');
      setError(message);
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const forgotPassword = useCallback(async (data: ForgotPasswordRequest) => {
    setIsLoading(true);
    setError(null);
    try {
      await api.post('/auth/forgot-password', data);
    } catch (err: unknown) {
      const message = extractErrorMessage(err, 'Erro ao enviar email. Tente novamente.');
      setError(message);
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
    setError(null);
  }, []);

  const userHasRole = useCallback(
    (role: UserRole) => hasRole(user, role),
    [user],
  );

  const userHasAnyRole = useCallback(
    (roles: UserRole[]) => hasAnyRole(user, roles),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      error,
      login,
      register,
      forgotPassword,
      logout,
      clearError,
      userHasRole,
      userHasAnyRole,
    }),
    [user, isAuthenticated, isLoading, error, login, register, forgotPassword, logout, clearError, userHasRole, userHasAnyRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Extracts a user-friendly error message from an unknown error.
 * Handles Axios error responses and generic Error instances.
 */
function extractErrorMessage(err: unknown, fallback: string): string {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const response = (err as { response?: { data?: { message?: string } } }).response;
    if (response?.data?.message) {
      return response.data.message;
    }
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return fallback;
}
